import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import "../spotlight-videos/SpotLight.css"
import axios from "axios";
import { BACKEND_URL } from "../common/Common";

//Navbar and Left Section Images
import xmark from '../../assets/home-images/right-section-images/xmark.png';
import leftarrow from '../../assets/offers/leftarrow.png';
import rightarrow from '../../assets/home-images/right-section-images/rightarrow.png';
import Play from '../../assets/spotlight/Play.png';
import Unmute from '../../assets/spotlight/Unmute.png';
import Pause from '../../assets/spotlight/Pause.png';
import Mute from '../../assets/spotlight/Mute.png';
import Navbar from "../common-component/navbar/Navbar";
import LeftSection from "../common-component/left-menu-section/LeftSection";

const SpotLights = () => {
    const { user_id } = useParams();
    const [searchParams] = useSearchParams();
    const spotlight_id = searchParams.get('spotlight_id');
    const navigate = useNavigate();

    const [allSpotlights, setAllSpotlights] = useState([]); // Store all spotlights
    const [spotlightData, setSpotlightData] = useState(null); // Currently active spotlight
    const [currentSpotlightIndex, setCurrentSpotlightIndex] = useState(0); // Index in allSpotlights
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingDurations, setIsLoadingDurations] = useState(false);
    const [error, setError] = useState(null);
    const [isMuted, setIsMuted] = useState(true);
    const [isPlaying, setIsPlaying] = useState(true);
    const [progress, setProgress] = useState(0); // Progress for current segment (0-100)
    const [isPaused, setIsPaused] = useState(false); // Pause state for story timer
    const [videoDurations, setVideoDurations] = useState({}); // Store video durations
    const videoRef = useRef(null);
    const progressTimerRef = useRef(null);
    const segmentStartTimeRef = useRef(null);
    const pausedProgressRef = useRef(0); // Store progress when paused
    const videoElementsRef = useRef({}); // Store video elements for duration loading
    const mainSectionRef = useRef(null);
    const spotlightRefs = useRef({}); // Store refs for each spotlight container
    const selectedSpotlightRef = useRef(null); // Ref for the initially selected spotlight
    const isInitialLoadRef = useRef(true); // Track if this is the initial load
    const STORY_SEGMENT_DURATION = 15000; // 15 seconds in milliseconds
    const VIDEO_SEGMENT_DURATION = 15; // 15 seconds in seconds

    // Fetch spotlight data
    useEffect(() => {
        const fetchSpotlightData = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const payload = {
                    login_user_id: 501,
                };
                const response = await axios.post(`${BACKEND_URL}website/get-spotlight-list`, payload);

                // Handle different possible response structures
                let data = response.data;
                if (data?.data && Array.isArray(data.data)) {
                    data = data.data;
                } else if (!Array.isArray(data)) {
                    data = [];
                }

                // Find the matching spotlight by user_id and spotlight_id
                const matchedIndex = data.findIndex((item) => {
                    const itemUserId = item.user_id || item.userId || item.id;
                    const itemSpotlightId = item.id || item.spotlight_id;
                    return (
                        Number(itemUserId) === Number(user_id) &&
                        (spotlight_id ? Number(itemSpotlightId) === Number(spotlight_id) : true)
                    );
                });

                if (matchedIndex !== -1) {
                    // Reorder array: move matched spotlight to first position
                    const reorderedData = [
                        data[matchedIndex],
                        ...data.slice(0, matchedIndex),
                        ...data.slice(matchedIndex + 1)
                    ];
                    setAllSpotlights(reorderedData);
                    setSpotlightData(reorderedData[0]);
                    setCurrentSpotlightIndex(0); // Now it's at index 0
                    isInitialLoadRef.current = true; // Mark as initial load
                } else if (data.length > 0) {
                    // If no match but we have data, show first spotlight
                    setAllSpotlights(data);
                    setSpotlightData(data[0]);
                    setCurrentSpotlightIndex(0);
                    isInitialLoadRef.current = true; // Mark as initial load
                } else {
                    setAllSpotlights([]);
                    setError("Spotlight not found");
                }
            } catch (error) {
                console.error("Error fetching spotlight data:", error);
                setError("Failed to load spotlight data");
            } finally {
                setIsLoading(false);
            }
        };

        if (user_id) {
            fetchSpotlightData();
        }
    }, [user_id, spotlight_id]);

    // Get raw media list from spotlight data
    const rawMediaList = useMemo(() => {
        if (!spotlightData) return [];

        // Extract media array from spotlight data
        if (spotlightData.media && Array.isArray(spotlightData.media)) {
            return spotlightData.media;
        } else if (spotlightData.media_items && Array.isArray(spotlightData.media_items)) {
            return spotlightData.media_items;
        } else if (spotlightData.videos && Array.isArray(spotlightData.videos)) {
            return spotlightData.videos;
        } else if (spotlightData.images && Array.isArray(spotlightData.images)) {
            return spotlightData.images;
        }

        return [];
    }, [spotlightData]);

    // Get all video URLs from ALL spotlights that need duration loading
    const videoUrls = useMemo(() => {
        const urls = new Set();
        allSpotlights.forEach((spotlight) => {
            const mediaArray = spotlight.media || spotlight.media_items || spotlight.videos || [];
            mediaArray.forEach((mediaItem) => {
                const mediaUrl = mediaItem.media || mediaItem.video_file || mediaItem.url;
                const mediaType = mediaItem.media_type || mediaItem.type || mediaItem.file_type;
                const hasVideoExtension = mediaUrl && mediaUrl.match(/\.(mp4|webm|ogg|mov|avi|mkv|flv|wmv|m4v)(\?|$)/i);
                const isVideoType = mediaType === "video" || mediaType === "Video" || mediaType === "VIDEO";
                const isVideo = isVideoType || hasVideoExtension;

                if (isVideo && mediaUrl && !videoDurations[mediaUrl]) {
                    urls.add(mediaUrl);
                }
            });
        });
        return Array.from(urls);
    }, [allSpotlights, videoDurations]);

    // Preload all video durations
    useEffect(() => {
        if (videoUrls.length === 0) {
            setIsLoadingDurations(false);
            return;
        }

        setIsLoadingDurations(true);
        const durations = {};
        let loadedCount = 0;
        const totalVideos = videoUrls.length;

        videoUrls.forEach((videoUrl) => {
            const video = document.createElement('video');
            video.preload = 'metadata';
            video.muted = true;

            const handleLoadedMetadata = () => {
                if (video.duration && isFinite(video.duration)) {
                    durations[videoUrl] = video.duration;
                } else {
                    // If duration is not available, assume 15 seconds
                    durations[videoUrl] = VIDEO_SEGMENT_DURATION;
                }
                loadedCount++;

                if (loadedCount === totalVideos) {
                    setVideoDurations(prev => ({ ...prev, ...durations }));
                    setIsLoadingDurations(false);
                }

                // Cleanup
                video.removeEventListener('loadedmetadata', handleLoadedMetadata);
                video.src = '';
            };

            video.addEventListener('loadedmetadata', handleLoadedMetadata);
            video.addEventListener('error', () => {
                // On error, assume 15 seconds
                durations[videoUrl] = VIDEO_SEGMENT_DURATION;
                loadedCount++;

                if (loadedCount === totalVideos) {
                    setVideoDurations(prev => ({ ...prev, ...durations }));
                    setIsLoadingDurations(false);
                }

                video.removeEventListener('loadedmetadata', handleLoadedMetadata);
                video.src = '';
            });

            video.src = videoUrl;
        });
    }, [videoUrls]);

    // Transform media list - split ALL videos into 15-second segments upfront
    // Only create segments once all video durations are loaded
    const mediaList = useMemo(() => {
        // Don't create segments if we're still loading durations
        if (isLoadingDurations && videoUrls.length > 0) {
            return [];
        }

        const expanded = [];

        rawMediaList.forEach((mediaItem) => {
            const mediaUrl = mediaItem.media || mediaItem.video_file || mediaItem.url;
            const mediaType = mediaItem.media_type || mediaItem.type || mediaItem.file_type;

            // Check if it's a video
            const hasVideoExtension = mediaUrl && mediaUrl.match(/\.(mp4|webm|ogg|mov|avi|mkv|flv|wmv|m4v)(\?|$)/i);
            const isVideoType = mediaType === "video" || mediaType === "Video" || mediaType === "VIDEO";
            const isVideo = isVideoType || hasVideoExtension;

            if (isVideo) {
                const videoUrl = mediaUrl;
                const duration = videoDurations[videoUrl] || VIDEO_SEGMENT_DURATION;

                // Always split videos into 15-second segments
                // If duration is known, split accordingly; otherwise assume 15 seconds
                let startTime = 0;
                let segmentIndex = 0;

                while (startTime < duration) {
                    const endTime = Math.min(startTime + VIDEO_SEGMENT_DURATION, duration);
                    expanded.push({
                        ...mediaItem,
                        media: videoUrl,
                        isVideo: true,
                        segmentIndex: segmentIndex++,
                        startTime: startTime,
                        endTime: endTime,
                        originalMedia: videoUrl,
                        originalIndex: rawMediaList.indexOf(mediaItem) // Track which original video this segment belongs to
                    });
                    startTime = endTime;
                }
            } else {
                // Images are added as-is (1 segment per image)
                expanded.push({
                    ...mediaItem,
                    media: mediaUrl || mediaItem.media,
                    isVideo: false,
                    originalIndex: rawMediaList.indexOf(mediaItem)
                });
            }
        });

        console.log(`📊 Total segments created: ${expanded.length}`);
        console.log(`📹 Video segments: ${expanded.filter(m => m.isVideo).length}`);
        console.log(`🖼️ Image segments: ${expanded.filter(m => !m.isVideo).length}`);

        return expanded;
    }, [rawMediaList, videoDurations, isLoadingDurations]);

    const currentMedia = mediaList[currentIndex];
    // Calculate media type directly - check isVideo property first (from mediaList transformation)
    // Then check media_type field, then file extension
    const isVideo = currentMedia?.isVideo === true ||
        (currentMedia?.media_type && String(currentMedia.media_type).toLowerCase() === "video") ||
        (currentMedia?.media && currentMedia.media.match(/\.(mp4|webm|ogg|mov|avi|mkv|flv|wmv|m4v)(\?|$)/i));

    // Image is anything that's not a video and has media
    const isImage = !isVideo && !!currentMedia?.media;

    // Create a stable media type identifier for effect dependency
    // Use the media URL as part of the key to ensure it changes when media changes
    const mediaTypeKey = `${currentIndex}-${currentMedia?.isVideo === true ? 'video' : currentMedia?.isVideo === false ? 'image' : isVideo ? 'video' : 'image'}-${currentMedia?.media ? currentMedia.media.substring(0, 50) : 'no-media'}`;

    // Reset index and progress when media list changes
    useEffect(() => {
        setCurrentIndex(0);
        setProgress(0);
        setIsPaused(false);
        if (progressTimerRef.current) {
            clearInterval(progressTimerRef.current);
            progressTimerRef.current = null;
        }
        // Pause video when media list changes to prevent auto-play issues
        if (videoRef.current) {
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
        }
    }, [mediaList.length]);

    const handleNext = () => {
        if (currentIndex < mediaList.length - 1) {
            // Clear progress timer
            if (progressTimerRef.current) {
                clearInterval(progressTimerRef.current);
                progressTimerRef.current = null;
            }
            if (videoRef.current) {
                videoRef.current.pause();
            }
            setProgress(0);
            setIsPaused(false);
            pausedProgressRef.current = 0;
            segmentStartTimeRef.current = null;
            setCurrentIndex(prev => prev + 1);
        }
    };

    const handlePrev = () => {
        if (currentIndex > 0) {
            // Clear progress timer
            if (progressTimerRef.current) {
                clearInterval(progressTimerRef.current);
                progressTimerRef.current = null;
            }
            if (videoRef.current) {
                videoRef.current.pause();
            }
            setProgress(0);
            setIsPaused(false);
            pausedProgressRef.current = 0;
            segmentStartTimeRef.current = null;
            setCurrentIndex(prev => prev - 1);
        }
    };

    const toggleMute = () => {
        if (videoRef.current) {
            videoRef.current.muted = !videoRef.current.muted;
            setIsMuted(videoRef.current.muted);
        }
    };

    const togglePlay = () => {
        if (videoRef.current) {
            if (videoRef.current.paused) {
                videoRef.current.play().catch(err => console.log(err));
                setIsPlaying(true);
                setIsPaused(false);
                // Resume progress timer - will be handled by the main progress timer effect
            } else {
                videoRef.current.pause();
                setIsPlaying(false);
                setIsPaused(true);
            }
        }
    };

    // Toggle pause for story timer (not just video)
    const toggleStoryPause = () => {
        setIsPaused(prev => {
            const newPaused = !prev;
            if (videoRef.current) {
                if (newPaused) {
                    videoRef.current.pause();
                    setIsPlaying(false);
                } else {
                    videoRef.current.play().catch(err => console.log(err));
                    setIsPlaying(true);
                }
            }
            return newPaused;
        });
    };

    // Instagram story logic: segments with progress tracking (uses actual segment duration)
    // Works for both videos and images
    useEffect(() => {
        if (!currentMedia) {
            // Clear timer if no media
            if (progressTimerRef.current) {
                clearInterval(progressTimerRef.current);
                progressTimerRef.current = null;
            }
            return;
        }

        // Calculate actual segment duration
        // For videos: use endTime - startTime (could be less than 15 seconds for last segment)
        // For images: use 15 seconds
        let actualSegmentDuration = STORY_SEGMENT_DURATION; // Default for images (15 seconds)
        if (isVideo && currentMedia.startTime !== undefined && currentMedia.endTime !== undefined) {
            const segmentDurationSeconds = currentMedia.endTime - currentMedia.startTime;
            actualSegmentDuration = segmentDurationSeconds * 1000; // Convert to milliseconds
        }

        // Clear any existing timer
        if (progressTimerRef.current) {
            clearInterval(progressTimerRef.current);
            progressTimerRef.current = null;
        }

        // If paused, store current progress and don't start timer
        if (isPaused) {
            pausedProgressRef.current = progress;
            return;
        }

        // Reset progress when switching between media (video to image or vice versa)
        setProgress(0);
        pausedProgressRef.current = 0;
        segmentStartTimeRef.current = Date.now();

        // Update progress every 50ms for smooth animation
        // This works for both images and videos
        progressTimerRef.current = setInterval(() => {
            if (!isPaused && segmentStartTimeRef.current) {
                const elapsed = Date.now() - segmentStartTimeRef.current;
                const newProgress = Math.min((elapsed / actualSegmentDuration) * 100, 100);
                setProgress(newProgress);

                // Auto-advance when segment duration elapsed
                // For videos: could be less than 15 seconds for last segment
                // For images: always 15 seconds
                if (newProgress >= 100) {
                    if (currentIndex < mediaList.length - 1) {
                        // Clear timer
                        if (progressTimerRef.current) {
                            clearInterval(progressTimerRef.current);
                            progressTimerRef.current = null;
                        }
                        if (videoRef.current) {
                            videoRef.current.pause();
                        }
                        setProgress(0);
                        pausedProgressRef.current = 0;
                        segmentStartTimeRef.current = null;
                        setCurrentIndex(prev => prev + 1);
                    }
                }
            }
        }, 50);

        return () => {
            if (progressTimerRef.current) {
                clearInterval(progressTimerRef.current);
                progressTimerRef.current = null;
            }
        };
    }, [currentIndex, currentMedia, isPaused, mediaList.length, isVideo, isImage, mediaTypeKey]);

    // Handle video playback - play segment from startTime to endTime
    useEffect(() => {
        if (isVideo && videoRef.current && currentMedia) {
            const video = videoRef.current;
            const videoSrc = currentMedia.originalMedia || currentMedia.media;
            const startTime = currentMedia?.startTime || 0;
            const endTime = currentMedia?.endTime || VIDEO_SEGMENT_DURATION;

            // Prevent video from playing beyond segment
            let segmentEnded = false;

            // Handle time update - stop at segment end time and advance if video ended
            const handleTimeUpdate = () => {
                // Clamp video time to segment bounds
                if (video.currentTime < startTime) {
                    video.currentTime = startTime;
                }
                
                if (video.currentTime >= endTime && !segmentEnded) {
                    segmentEnded = true;
                    video.pause();
                    video.currentTime = endTime; // Ensure we're exactly at end time
                    
                    // If video segment ended, advance immediately (especially for short segments)
                    // This ensures fast advancement for segments less than 15 seconds
                    if (currentIndex < mediaList.length - 1) {
                        // Clear progress timer
                        if (progressTimerRef.current) {
                            clearInterval(progressTimerRef.current);
                            progressTimerRef.current = null;
                        }
                        setProgress(0);
                        pausedProgressRef.current = 0;
                        segmentStartTimeRef.current = null;
                        setCurrentIndex(prev => prev + 1);
                    }
                }
            };

            // Handle video seeking - prevent seeking beyond segment
            const handleSeeking = () => {
                if (video.currentTime < startTime) {
                    video.currentTime = startTime;
                }
                if (video.currentTime > endTime) {
                    video.currentTime = endTime;
                }
            };

            // Handle video ended - prevent looping
            const handleEnded = () => {
                if (!segmentEnded && currentIndex < mediaList.length - 1) {
                    segmentEnded = true;
                    // Clear progress timer
                    if (progressTimerRef.current) {
                        clearInterval(progressTimerRef.current);
                        progressTimerRef.current = null;
                    }
                    setProgress(0);
                    pausedProgressRef.current = 0;
                    segmentStartTimeRef.current = null;
                    setCurrentIndex(prev => prev + 1);
                }
            };

            // Only reload if the video source changed (different video, not just different segment)
            const isNewVideo = video.src !== videoSrc || !video.src;
            let handleCanPlay = null;
            let handleLoadedData = null;

            // Reset segment ended flag
            segmentEnded = false;

            if (isNewVideo) {
                video.pause();
                video.src = videoSrc;
                video.load();

                // Wait for video to load, then set start time
                handleCanPlay = () => {
                    if (video.readyState >= 2) {
                        video.currentTime = startTime;
                        const playPromise = video.play();
                        if (playPromise !== undefined) {
                            playPromise.catch((err) => {
                                if (err.name !== "AbortError") {
                                    console.error("Video play error:", err);
                                }
                            });
                        }
                    }
                };

                handleLoadedData = () => {
                    if (video.readyState >= 2 && currentMedia?.startTime !== undefined) {
                        video.currentTime = currentMedia.startTime;
                    }
                };

                video.addEventListener('canplay', handleCanPlay, { once: true });
                video.addEventListener('loadeddata', handleLoadedData, { once: true });
            } else {
                // Same video, different segment - just update currentTime without reloading
                video.pause();
                video.currentTime = startTime;
                const playPromise = video.play();
                if (playPromise !== undefined) {
                    playPromise.catch((err) => {
                        if (err.name !== "AbortError") {
                            console.error("Video play error:", err);
                        }
                    });
                }
            }

            video.addEventListener('timeupdate', handleTimeUpdate);
            video.addEventListener('seeking', handleSeeking);
            video.addEventListener('ended', handleEnded);

            return () => {
                if (handleCanPlay) {
                    video.removeEventListener('canplay', handleCanPlay);
                }
                if (handleLoadedData) {
                    video.removeEventListener('loadeddata', handleLoadedData);
                }
                video.removeEventListener('timeupdate', handleTimeUpdate);
                video.removeEventListener('seeking', handleSeeking);
                video.removeEventListener('ended', handleEnded);
            };
        }
    }, [currentIndex, isVideo, currentMedia, mediaList.length]);

    // Separate effect to update muted state without reloading video
    useEffect(() => {
        if (isVideo && videoRef.current) {
            videoRef.current.muted = isMuted;
        }
    }, [isMuted, isVideo]);

    // Get media list for a specific spotlight
    const getMediaListForSpotlight = useCallback((spotlight) => {
        if (!spotlight) return [];

        const rawMediaList = [];
        if (spotlight.media && Array.isArray(spotlight.media)) {
            rawMediaList.push(...spotlight.media);
        } else if (spotlight.media_items && Array.isArray(spotlight.media_items)) {
            rawMediaList.push(...spotlight.media_items);
        } else if (spotlight.videos && Array.isArray(spotlight.videos)) {
            rawMediaList.push(...spotlight.videos);
        } else if (spotlight.images && Array.isArray(spotlight.images)) {
            rawMediaList.push(...spotlight.images);
        }

        const expanded = [];
        rawMediaList.forEach((mediaItem) => {
            const mediaUrl = mediaItem.media || mediaItem.video_file || mediaItem.url;
            const mediaType = mediaItem.media_type || mediaItem.type || mediaItem.file_type;

            const hasVideoExtension = mediaUrl && mediaUrl.match(/\.(mp4|webm|ogg|mov|avi|mkv|flv|wmv|m4v)(\?|$)/i);
            const isVideoType = mediaType === "video" || mediaType === "Video" || mediaType === "VIDEO";
            const isVideo = isVideoType || hasVideoExtension;

            if (isVideo) {
                const videoUrl = mediaUrl;
                const duration = videoDurations[videoUrl] || VIDEO_SEGMENT_DURATION;

                let startTime = 0;
                let segmentIndex = 0;

                while (startTime < duration) {
                    const endTime = Math.min(startTime + VIDEO_SEGMENT_DURATION, duration);
                    expanded.push({
                        ...mediaItem,
                        media: videoUrl,
                        isVideo: true,
                        segmentIndex: segmentIndex++,
                        startTime: startTime,
                        endTime: endTime,
                        originalMedia: videoUrl,
                        originalIndex: rawMediaList.indexOf(mediaItem)
                    });
                    startTime = endTime;
                }
            } else {
                expanded.push({
                    ...mediaItem,
                    media: mediaUrl || mediaItem.media,
                    isVideo: false,
                    originalIndex: rawMediaList.indexOf(mediaItem)
                });
            }
        });

        return expanded;
    }, [videoDurations]);

    // Store video duration when metadata loads (fallback if preloading missed it)
    const handleLoadedMetadata = () => {
        if (videoRef.current && currentMedia) {
            const duration = videoRef.current.duration;
            const videoUrl = currentMedia.originalMedia || currentMedia.media;

            // Store duration if we don't have it yet or if it's significantly different
            if (videoUrl && duration && isFinite(duration) &&
                (!videoDurations[videoUrl] || Math.abs(videoDurations[videoUrl] - duration) > 0.1)) {
                setVideoDurations(prev => ({
                    ...prev,
                    [videoUrl]: duration
                }));
            }
        }
    };

    // Use IntersectionObserver to detect which spotlight is in view (like VideoPost.js)
    useEffect(() => {
        if (allSpotlights.length === 0) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const spotlightIndex = parseInt(entry.target.dataset.spotlightIndex);
                    
                    if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                        // Only switch if it's a different spotlight and not during initial load
                        if (!isNaN(spotlightIndex) && 
                            spotlightIndex !== currentSpotlightIndex && 
                            allSpotlights[spotlightIndex]) {
                            
                            // Skip if it's the initial load and this is the selected spotlight
                            if (isInitialLoadRef.current && spotlightIndex === 0) {
                                return;
                            }
                            
                            // Switch to this spotlight
                            setSpotlightData(allSpotlights[spotlightIndex]);
                            setCurrentSpotlightIndex(spotlightIndex);
                            setCurrentIndex(0);
                            setProgress(0);
                            setIsPaused(false);
                            pausedProgressRef.current = 0;
                            segmentStartTimeRef.current = null;
                            
                            // Clear progress timer
                            if (progressTimerRef.current) {
                                clearInterval(progressTimerRef.current);
                                progressTimerRef.current = null;
                            }
                            
                            // Pause video
                            if (videoRef.current) {
                                videoRef.current.pause();
                            }
                        }
                    } else if (!entry.isIntersecting && spotlightIndex === currentSpotlightIndex) {
                        // Pause when spotlight goes out of view
                        if (videoRef.current) {
                            videoRef.current.pause();
                        }
                        if (progressTimerRef.current) {
                            clearInterval(progressTimerRef.current);
                            progressTimerRef.current = null;
                        }
                        setIsPaused(true);
                    }
                });
            },
            {
                threshold: [0, 0.5, 1], // Trigger at 0%, 50%, and 100% visibility (like VideoPost.js)
                rootMargin: '0px' // No margin for more precise detection
            }
        );

        // Observe all spotlight containers
        const observeSpotlights = () => {
            Object.values(spotlightRefs.current).forEach((ref) => {
                if (ref) observer.observe(ref);
            });
        };

        observeSpotlights();

        // Use MutationObserver to handle dynamically added elements (like VideoPost.js)
        const mutationObserver = new MutationObserver(observeSpotlights);
        if (mainSectionRef.current) {
            mutationObserver.observe(mainSectionRef.current, { childList: true, subtree: true });
        }

        return () => {
            observer.disconnect();
            mutationObserver.disconnect();
        };
    }, [allSpotlights, currentSpotlightIndex]);

    // Scroll to selected spotlight on initial load (only once)
    const hasScrolledToInitialRef = useRef(false);
    useEffect(() => {
        if (allSpotlights.length > 0 && currentSpotlightIndex >= 0 && selectedSpotlightRef.current && !hasScrolledToInitialRef.current) {
            // Use a longer timeout to ensure DOM is ready
            const timeoutId = setTimeout(() => {
                if (selectedSpotlightRef.current && mainSectionRef.current) {
                    selectedSpotlightRef.current.scrollIntoView({
                        behavior: 'smooth', // Use 'smooth' for better UX (like VideoPost.js)
                        block: 'start'
                    });
                    hasScrolledToInitialRef.current = true;
                    // Mark initial load as complete after scroll
                    setTimeout(() => {
                        isInitialLoadRef.current = false;
                    }, 1000);
                }
            }, 500); // Increased timeout to match VideoPost.js
            return () => clearTimeout(timeoutId);
        }
    }, [allSpotlights.length, currentSpotlightIndex]);

    return (
        <div>
            <Navbar />

            <div className="spotlight-main-layout">
                <LeftSection />

                <main className="spotlight-middle-section" ref={mainSectionRef}>
                    {/* Close button - fixed position */}
                    <div className="spotlight-close-button" onClick={() => navigate(-1)}>
                        <img src={xmark} alt="close" />
                    </div>

                    {isLoading || isLoadingDurations ? (
                        <div className="spotlight-loading">
                            <p>Loading spotlight{isLoadingDurations ? ' (calculating segments...)' : ''}...</p>
                        </div>
                    ) : error ? (
                        <div className="spotlight-error">
                            <p>{error}</p>
                        </div>
                    ) : allSpotlights.length === 0 ? (
                        <div className="spotlight-empty">
                            <p>No spotlights found.</p>
                        </div>
                    ) : (
                        <>
                            {/* Render all spotlights vertically */}
                            {allSpotlights.map((spotlight, spotlightIdx) => {
                                const spotlightMediaList = getMediaListForSpotlight(spotlight);
                                const isActiveSpotlight = spotlightIdx === currentSpotlightIndex;
                                const activeMediaList = isActiveSpotlight ? mediaList : spotlightMediaList;
                                const activeCurrentIndex = isActiveSpotlight ? currentIndex : 0;
                                const activeProgress = isActiveSpotlight ? progress : 0;
                                const activeIsPaused = isActiveSpotlight ? isPaused : true;

                                // Get current media for this spotlight
                                const spotlightCurrentMedia = activeMediaList[activeCurrentIndex];
                                const spotlightIsVideo = spotlightCurrentMedia?.isVideo ||
                                    spotlightCurrentMedia?.media_type === "video" ||
                                    (spotlightCurrentMedia?.media && spotlightCurrentMedia.media.match(/\.(mp4|webm|ogg|mov|avi|mkv|flv|wmv|m4v)(\?|$)/i));
                                const spotlightIsImage = !spotlightIsVideo && spotlightCurrentMedia?.media;

                                return (
                                    <div
                                        key={`spotlight-${spotlight.user_id || spotlight.id}-${spotlight.id || spotlightIdx}`}
                                        className="spotlight-container"
                                        ref={(el) => {
                                            if (el) {
                                                spotlightRefs.current[spotlightIdx] = el;
                                                // Set ref for selected spotlight (update whenever currentSpotlightIndex changes)
                                                if (spotlightIdx === currentSpotlightIndex) {
                                                    selectedSpotlightRef.current = el;
                                                }
                                            } else {
                                                // Cleanup
                                                delete spotlightRefs.current[spotlightIdx];
                                                if (selectedSpotlightRef.current === el) {
                                                    selectedSpotlightRef.current = null;
                                                }
                                            }
                                        }}
                                        data-spotlight-index={spotlightIdx}
                                    >
                                        {activeMediaList.length > 0 ? (
                                            <>
                                                {/* LEFT ARROW */}
                                                {activeMediaList.length > 1 && activeCurrentIndex > 0 && isActiveSpotlight && (
                                                    <div className="arrow-left" onClick={handlePrev}>
                                                        <img src={leftarrow} alt="left" />
                                                    </div>
                                                )}

                                                <div className="spotlight-gallery-container">
                                                    {/* Progress bars - Instagram story style */}
                                                    <div className="story-progress">
                                                        {activeMediaList.map((_, idx) => {
                                                            let segmentProgress = 0;
                                                            if (idx < activeCurrentIndex) {
                                                                segmentProgress = 100;
                                                            } else if (idx === activeCurrentIndex && isActiveSpotlight) {
                                                                segmentProgress = activeProgress;
                                                            }
                                                            return (
                                                                <div key={idx} className="story-segment">
                                                                    <div
                                                                        className="story-segment-bar"
                                                                        style={{
                                                                            width: `${segmentProgress}%`,
                                                                            transition: idx === activeCurrentIndex && !activeIsPaused && isActiveSpotlight ? 'width 0.05s linear' : 'none'
                                                                        }}
                                                                    ></div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>

                                                    {/* Vendor info and controls */}
                                                    <div className="vendor-and-media">
                                                        <div className="vendor-list">
                                                            <Link
                                                                to={`/vendordetails/${spotlight.user_id}`}
                                                                className="remove-underline"
                                                            >
                                                                <div className="vendor-item">
                                                                    <img
                                                                        src={spotlight.user_image || spotlight.vendor_cover_image}
                                                                        alt="vendor cover"
                                                                    />
                                                                    <h4>{spotlight.user_name || spotlight.vendor_name}</h4>
                                                                </div>
                                                            </Link>
                                                        </div>
                                                        <div className="media-button">
                                                            {spotlightIsVideo && isActiveSpotlight && (
                                                                <>
                                                                    <img
                                                                        src={isMuted ? Mute : Unmute}
                                                                        alt={isMuted ? "unmute" : "mute"}
                                                                        className="play-button"
                                                                        onClick={toggleMute}
                                                                    />
                                                                    <img
                                                                        src={isPlaying ? Pause : Play}
                                                                        alt={isPlaying ? "pause" : "play"}
                                                                        className="play-button"
                                                                        onClick={togglePlay}
                                                                    />
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Media container */}
                                                    <div
                                                        className="spotlight-media-wrapper"
                                                        onClick={isActiveSpotlight ? toggleStoryPause : undefined}
                                                        style={{ cursor: isActiveSpotlight ? 'pointer' : 'default' }}
                                                    >
                                                        {isActiveSpotlight ? (
                                                            // Active spotlight - full interactive player
                                                            <>
                                                                {spotlightIsVideo ? (
                                                                    <video
                                                                        ref={videoRef}
                                                                        src={spotlightCurrentMedia?.originalMedia || spotlightCurrentMedia?.media || spotlightCurrentMedia?.video_file || spotlightCurrentMedia?.url}
                                                                        className="spotlight-video"
                                                                        autoPlay
                                                                        muted={isMuted}
                                                                        playsInline
                                                                        loop={false}
                                                                        onPlay={() => {
                                                                            setIsPlaying(true);
                                                                            setIsPaused(false);
                                                                        }}
                                                                        onPause={() => {
                                                                            setIsPlaying(false);
                                                                            setIsPaused(true);
                                                                        }}
                                                                        onLoadedMetadata={handleLoadedMetadata}
                                                                    />
                                                                ) : spotlightIsImage ? (
                                                                    <div className="spotlight-image-container">
                                                                        <img
                                                                            src={spotlightCurrentMedia?.media || spotlightCurrentMedia?.url || spotlightCurrentMedia?.post_images}
                                                                            alt="spotlight media"
                                                                            className="spotlight-image"
                                                                        />
                                                                    </div>
                                                                ) : null}
                                                            </>
                                                        ) : (
                                                            // Inactive spotlight - static preview (paused video or image)
                                                            <>
                                                                {spotlightIsVideo ? (
                                                                    <video
                                                                        src={spotlightCurrentMedia?.originalMedia || spotlightCurrentMedia?.media || spotlightCurrentMedia?.video_file || spotlightCurrentMedia?.url}
                                                                        className="spotlight-video"
                                                                        muted
                                                                        playsInline
                                                                        preload="metadata"
                                                                        style={{ pointerEvents: 'none' }}
                                                                    />
                                                                ) : spotlightIsImage ? (
                                                                    <div className="spotlight-image-container">
                                                                        <img
                                                                            src={spotlightCurrentMedia?.media || spotlightCurrentMedia?.url || spotlightCurrentMedia?.post_images}
                                                                            alt="spotlight preview"
                                                                            className="spotlight-image"
                                                                        />
                                                                    </div>
                                                                ) : null}
                                                            </>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* RIGHT ARROW */}
                                                {activeMediaList.length > 1 && activeCurrentIndex < activeMediaList.length - 1 && isActiveSpotlight && (
                                                    <div className="arrow-right" onClick={handleNext}>
                                                        <img src={rightarrow} alt="right" />
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="spotlight-empty">
                                                <p>No media found for this spotlight.</p>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </>
                    )}
                </main>
            </div>
        </div>
    );
};

export default SpotLights;
